import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { COUNTRY_CODE_TOKEN, isESCountry, isUsdCountry } from '../config';

export type Currency = 'EUR' | 'USD';

const STORAGE_KEY = 'userCountry';
const GEO_COOKIE = 'geo_country';

@Injectable({
    providedIn: 'root'
})
export class GeoPricingService {

    private readonly platformId = inject(PLATFORM_ID);

    /** País inyectado por el servidor SSR vía COUNTRY_CODE_TOKEN. */
    private readonly ssrCountryCode = inject(COUNTRY_CODE_TOKEN);

    private readonly _countryCode$ = new BehaviorSubject<string>('');
    private readonly _currency$ = new BehaviorSubject<Currency>('EUR');

    /** Código ISO 3166-1 alpha-2 del país activo */
    readonly countryCode$ = this._countryCode$.asObservable();

    /** Moneda activa: 'USD' para US/CA, 'EUR' para el resto. */
    readonly currency$ = this._currency$.asObservable();

    /**
     * Inicializa el servicio con la siguiente prioridad:
     *  1. localStorage (preferencia guardada manualmente por el usuario)
     *  2. Cookie 'geo_country' escrita por el servidor SSR
     *  3. COUNTRY_CODE_TOKEN (solo disponible en el servidor)
     *  4. '' → default EUR + EN
     */
    initialize(): void {
        const stored = this.getStoredCountry();
        const cookie = this.getGeoCookie();
        const countryCode = stored || cookie || this.ssrCountryCode || '';
        this.applyCountry(countryCode);
    }

    /**
     * Override manual del país (desde el dropdown del usuario).
     * Guarda la preferencia en localStorage y actualiza el estado reactivo.
     */
    setCountry(countryCode: string): void {
        const code = countryCode.toUpperCase();
        this.saveCountry(code);
        this.applyCountry(code);
    }

    getCountryCode(): string {
        return this._countryCode$.value;
    }

    getCurrency(): Currency {
        return this._currency$.value;
    }

    isUsd(): boolean {
        return this._currency$.value === 'USD';
    }

    isSpanish(): boolean {
        return isESCountry(this._countryCode$.value);
    }

    // Private helpers
    private applyCountry(countryCode: string): void {
        this._countryCode$.next(countryCode);
        this._currency$.next(isUsdCountry(countryCode) ? 'USD' : 'EUR');
    }

    private getGeoCookie(): string {
        if (!isPlatformBrowser(this.platformId)) return '';
        const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${GEO_COOKIE}=([^;]+)`));
        return match ? match[1].toUpperCase() : '';
    }

    private getStoredCountry(): string {
        if (isPlatformBrowser(this.platformId)) {
            return localStorage.getItem(STORAGE_KEY) ?? '';
        }
        return '';
    }

    private saveCountry(countryCode: string): void {
        if (isPlatformBrowser(this.platformId)) {
            localStorage.setItem(STORAGE_KEY, countryCode);
        }
    }
}
